import React, { useEffect, useMemo, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarExport,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, FormControl } from '@mui/material';
import LocationApiService from '../api/LocationApiService';
import EditMenuButton from './Modal/EditMenuButton';
import EditLocationModal from './Modal/Admin/EditLocationModal';
import ConfirmDeleteDialogHazard from './Modal/ConfirmDeleteHazard';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from './Common/SnackbarProvider';

const locationApiService = new LocationApiService();

const Locations = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(100);
    const [currentSelectedLocation, setCurrentSelectedLocation] = useState();
    const [openEditLocationModal, setOpenEditLocationModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
            </GridToolbarContainer>
        );
    }

    const columns = useMemo(() => [
        {
            field: 'pjId',
            headerName: t('common.id'),
            flex: 0.5,
        },
        {
            field: 'ogName',
            headerName: t('fieldName.organization'),
            flex: 1.5,
            valueGetter: (params) => params.row.pjOrgDto?.ogName
        },
        {
            field: 'pjName',
            headerName: t('fieldName.location'),
            flex: 1.5,
        },
        {
            field: 'pjActive',
            headerName: t('common.active'),
            flex: 1.5,
            renderCell: (params) => params.row.pjActive > 0 ? t('common.yShort') : t('common.nShort')
        },
        {
            field: 'pjDefault',
            headerName: t('locationsPage.column.default'),
            flex: 1.5,
            renderCell: (params) => params.row.pjDefault > 0 ? t('common.yShort') : t('common.nShort')
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <EditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setCurrentSelectedLocation}
                    setShowEditDialog={setOpenEditLocationModal}
                    onEditFunc={() => {
                        setOpenEditLocationModal(true);
                        setCurrentSelectedLocation(params.row);
                    }}
                />;
            },
        },
    ], [t]);

    useEffect(() => {
        async function setupLocations () {
            setLoading(true);
            await populateLocations();
            setLoading(false);
        }

        setupLocations();
    }, []);

    async function populateLocations () {
        let locationResponse = await locationApiService.getLocations();
        setLocations(locationResponse.data);
    }

    const dialogTitle = () => {
        return t('locationsPage.confirmDeleteTitle', { name: currentSelectedLocation?.pjName ?? '' });
    };

    const dialogLabel = () => {
        return t('locationsPage.confirmDeleteLabel', { name: currentSelectedLocation?.pjName ?? '' });
    };

    async function onDeleteLocationClick (deleteValue) {
        if (deleteValue) {
            let deleteLocationResponse = await locationApiService.deleteLocation(currentSelectedLocation);
            if (deleteLocationResponse.success === true)
            {
                snackbar.success(t('locationsPage.toast.deleted', { name: currentSelectedLocation.pjName }));
                await populateLocations();
            }
        }

        setShowDeleteDialog(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))' }}>
            <Typography variant="h4" sx={{ marginBottom: '16px' }}>
                {t('locationsPage.title')}
            </Typography>
            <FormControl sx={{ marginBottom: '8px' }}>
                <Button size={'large'} variant="contained" onClick={() => setOpenEditLocationModal(true)} >{t('locationsPage.createButton')}</Button>
            </FormControl>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataGridPro
                    rows={locations}
                    columns={columns}
                    loading={loading}
                    pagination
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    getRowId={(row) => row.pjId}
                    components={{
                        Toolbar: CustomToolbar,
                    }}
                    getRowHeight={() => 'auto'}
                    rowsPerPageOptions={[10, 25, 50, 100, 200]}
                />
            </Box>
            <EditLocationModal
                open={openEditLocationModal}
                currentLocation={currentSelectedLocation}
                onCancel={() => {
                    setOpenEditLocationModal(false);
                    setCurrentSelectedLocation(null);
                }}
                onSave={async () => {
                    await populateLocations();
                    setOpenEditLocationModal(false);
                    setCurrentSelectedLocation(null);
                }}
            />
            <ConfirmDeleteDialogHazard
                id="confirm-delete"
                keepMounted
                value={currentSelectedLocation}
                open={showDeleteDialog}
                onClose={onDeleteLocationClick}
                deleteTitle={dialogTitle()}
                deleteLabel={dialogLabel()}
            ></ConfirmDeleteDialogHazard>
        </Box>
    );
};
export default Locations;
